import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Clock4 } from 'lucide-react';
import SearchFilters from '../shared/SearchFilters.jsx';
import Header from '../shared/Header.jsx';
import SessionCard from './SessionCard.jsx';
import SessionModal from '../shared/SessionModal.jsx';
import SpeakerModal from '../shared/SpeakerModal.jsx';
import { getAvailableFilters } from '../utils/filters.js';
import { buildSessionSearchFields } from '../utils/search.js';
import {
  filterSessionGeneratorFn,
  getFilteredScheduleData,
  getCurrentDayDataFromFiltered,
  getAllFilteredSessionsCount,
  getSessionDataForModal,
  getSpeakerDataForModal,
  getEnhancedSessionForCalendar,
} from './utils.js';
import { WIDGET_TYPES, MODAL_TYPES } from '../constants.js';
import getVisibleFieldIdsOfType, { getVisibleFieldsOfTypeById } from '../utils/get-visible-fields-of-widget.js';
import { useModalStack } from '../hooks/useModalStack.js';
import {
  useOpenRecordFromUrl,
  useHighlightedSessionIds,
  URL_RECORD_PARAMS,
} from '../hooks/useOpenRecordFromUrl.js';
import { useAddToCalendarScript } from '../hooks/useAddToCalendarScript.js';
import {
  firstDatedHighlight,
  findScheduleDayIndex,
  scheduleScrollSessionIntoView,
} from '../utils/scroll-session-into-view.js';
import Footer from '../shared/Footer.jsx';
import NoResults from '../shared/NoResults.jsx';
import Loading from '../shared/Loading.jsx';
import SubsessionModal from '../shared/SubsessionModal.jsx';
import { getSubsessionDataForModal } from '../utils/subsession-modal.js';
import { useI18n } from '../i18n/index.jsx';

/**
 * @param {import('../types/async-data.js').AsyncDataResponse<import('../types/async-data.js').ScheduleScopeData>} data
 * @param {boolean} loading
 */
const ScheduleWidget = ({ data = null, loading }) => {
  const { t } = useI18n();
  const [scheduleData, setScheduleData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const scrollWrapperRef = useRef(null);
  const { visibleModal, openModal, closeModal } = useModalStack();
  const useModals = data?.useModals || false;
  // Prefer live `data` for deep links — `scheduleData` is synced in an effect
  // and can lag one frame after loading flips false (SB-7560 race).
  const sessionsById =
    scheduleData?.data?.sessionsById || data?.data?.sessionsById || {};
  const sessionsReady = Object.keys(sessionsById).length > 0;

  useOpenRecordFromUrl({
    ready: !loading && useModals && sessionsReady,
    shortcuts: [
      {
        param: URL_RECORD_PARAMS.SESSION,
        modalType: MODAL_TYPES.SESSION,
        exists: id => Boolean(sessionsById[id]),
      },
    ],
    openModal,
  });
  const highlightedSessionIds = useHighlightedSessionIds({
    ready: !loading && sessionsReady,
  });

  useEffect(() => {
    setScheduleData(data);
  }, [data]);

  useEffect(() => {
    if (loading || !highlightedSessionIds.size) return;
    const pick = firstDatedHighlight(highlightedSessionIds, id =>
      findScheduleDayIndex(scheduleData || data, id)
    );
    if (!pick) return;

    if (pick.dayIndex >= 0) {
      if (pick.dayIndex !== selectedDay) {
        setSelectedDay(pick.dayIndex);
        return;
      }
      scheduleScrollSessionIntoView(() => scrollWrapperRef.current, pick.id);
      return;
    }

    if (useModals && sessionsById[pick.id]) {
      openModal(MODAL_TYPES.SESSION, pick.id);
    }
  }, [
    loading,
    highlightedSessionIds,
    scheduleData,
    data,
    selectedDay,
    useModals,
    sessionsById,
    openModal,
  ]);

  const availableFilters = useMemo(
    () => getAvailableFilters(scheduleData?.data?.filterOptions, scheduleData?.embedFields),
    [scheduleData?.data?.filterOptions, scheduleData?.embedFields]
  );
  const visibleFields = getVisibleFieldIdsOfType(scheduleData?.groupedFields, WIDGET_TYPES.SCHEDULE);
  const visibleSpeakerFields = getVisibleFieldIdsOfType(scheduleData?.groupedFields, WIDGET_TYPES.SPEAKERS);
  const visibleSpeakerFieldsById = getVisibleFieldsOfTypeById(scheduleData?.groupedFields, WIDGET_TYPES.SPEAKERS);
  const visibleSessionFields = useMemo(
    () => getVisibleFieldIdsOfType(scheduleData?.groupedFields, WIDGET_TYPES.SESSIONS),
    [scheduleData?.groupedFields]
  );
  const visibleSessionFieldsById = getVisibleFieldsOfTypeById(scheduleData?.groupedFields, WIDGET_TYPES.SESSIONS);
  // SB-10242: the itinerary card labels CEU Credits from the Session selection
  // (where a custom label lives), then the Agenda selection.
  const cardLabelFieldsById = [
    visibleSessionFieldsById,
    getVisibleFieldsOfTypeById(scheduleData?.groupedFields, WIDGET_TYPES.SCHEDULE),
  ];
  const primaryColor = scheduleData?.primaryColor;
  const isDarkTheme = scheduleData?.isDarkTheme || false;
  const showAddToCalendar = scheduleData?.data?.filterOptions?.show_add_to_calendar || false;
  const showSessionSponsors = scheduleData?.data?.filterOptions?.show_session_sponsors ?? false;
  const { isLoaded: isAddToCalendarScriptLoaded } = useAddToCalendarScript(showAddToCalendar);

  const handleSearch = query => {
    setSearchQuery(query);
  };

  const handleFilterChange = (filterType, selectedValues) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterType]: selectedValues,
    }));
  };
  const handleClearFilters = () => {
    setSearchQuery('');
    setActiveFilters({});
  };

  const handleOpenSession = sessionId => {
    openModal(MODAL_TYPES.SESSION, sessionId);
  };

  const handleSpeakerClick = speakerId => {
    openModal(MODAL_TYPES.SPEAKER, speakerId);
  };

  // SB-8856: subsession cards/rows push a subsession detail modal.
  const handleSubsessionClick = subsessionId => {
    openModal(MODAL_TYPES.SUBSESSION, subsessionId);
  };

  const handleCloseModal = (type, id) => {
    closeModal(type, id);
  };

  // Filter sessions based on search query and active filters
  const searchFields = useMemo(
    () => buildSessionSearchFields(availableFilters, visibleSessionFields),
    [availableFilters, visibleSessionFields]
  );
  const filterSessionFn = useMemo(
    () => filterSessionGeneratorFn(activeFilters, searchQuery, availableFilters, searchFields),
    [activeFilters, searchQuery, availableFilters, searchFields]
  );
  const originalDayTabs = scheduleData?.data?.orderedDateKeys || [];
  const originalTablesByDay = scheduleData?.data?.tablesByDay || {};

  // Pre-filter all days and sessions once for optimal performance
  const { filteredDayTabs, filteredTablesByDay } = getFilteredScheduleData(
    originalDayTabs,
    originalTablesByDay,
    filterSessionFn
  );

  // Get current day data from pre-filtered data
  // Reset selectedDay to 0 if current selection is out of bounds after filtering
  const validSelectedDay = selectedDay >= filteredDayTabs.length ? 0 : selectedDay;
  const currentDayData = getCurrentDayDataFromFiltered(filteredDayTabs, filteredTablesByDay, validSelectedDay);

  const renderTimeHeader = time => (
    <div className="schedule-itinerary__time-header">
      <div className="schedule-itinerary__time-header-content">
        <Clock4 />
        <span className="schedule-itinerary__time-text">{time}</span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="schedule-itinerary__loading">
        <Loading size="large" colorVariant="primary" text={t('loading_data')} />
      </div>
    );
  }

  if (!scheduleData && !loading) {
    return <div className="schedule-itinerary__no-data">{t('no_sessions_available')}</div>;
  }

  return (
    <div
      className={`schedule-itinerary ${isDarkTheme ? 'theme-dark' : ''}`}
      style={primaryColor ? { '--color-primary': primaryColor } : {}}
    >
      <Header
        eventName={scheduleData?.eventName}
        primaryColor={primaryColor}
        hidden={Boolean(scheduleData?.hideHeader)}
      />

      <div className="schedule-itinerary__main">
        <div className="schedule-itinerary__page-header">
          <h2 className="schedule-itinerary__page-title">{t('itinerary')}</h2>

          <div className="schedule-itinerary__controls">
            <SearchFilters
              searchQuery={searchQuery}
              onSearch={handleSearch}
              availableFilters={availableFilters}
              activeFilters={activeFilters}
              onFilterChange={handleFilterChange}
              handleClearFilters={handleClearFilters}
              filteredItemsCount={getAllFilteredSessionsCount(filteredDayTabs, filteredTablesByDay)}
              showSearch={scheduleData?.data?.filterOptions?.search_speaker_session_by_name || false}
            />

            {filteredDayTabs.length > 0 && (
              <div className="schedule-itinerary__day-tabs">
                {filteredDayTabs.map((day, index) => (
                  <button
                    key={index}
                    className={`schedule-itinerary__day-tab ${validSelectedDay === index ? 'schedule-itinerary__day-tab--active' : ''}`}
                    onClick={() => setSelectedDay(index)}
                  >
                    {/* SB-9214: keys are Western YYYY-MM-DD (SB-9031); the
                        organizer-facing label lives on dayTitle, like agenda. */}
                    {filteredTablesByDay[day]?.dayTitle || day}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="schedule-itinerary__content" ref={scrollWrapperRef}>
          {(!currentDayData || currentDayData?.table?.sessionsByTime?.length === 0) && (
            <NoResults
              widgetItem={t('items_sessions')}
              searchQuery={searchQuery}
              hasActiveFiltersOrSearch={Object.keys(activeFilters).length > 0 || searchQuery}
              handleClearFilters={handleClearFilters}
            />
          )}

          {currentDayData?.table?.sessionsByTime?.map((timeSlot, index) => (
            <div key={index} className="schedule-itinerary__time-slot">
              {renderTimeHeader(timeSlot.time)}
              {timeSlot.timeItems.map(session => (
                <SessionCard
                  visibleSessionFields={visibleFields}
                  visibleSpeakerFields={visibleSpeakerFields}
                  labelFieldsById={cardLabelFieldsById}
                  primaryColor={primaryColor}
                  key={session.id}
                  session={session}
                  isHighlighted={highlightedSessionIds.has(session.id)}
                  sessionForCalendar={getEnhancedSessionForCalendar(session.id, scheduleData?.data?.sessionsById)}
                  onSessionClick={handleOpenSession}
                  onSpeakerClick={handleSpeakerClick}
                  onSubsessionClick={useModals ? handleSubsessionClick : undefined}
                  showAddToCalendar={showAddToCalendar && isAddToCalendarScriptLoaded}
                  showSessionSponsors={showSessionSponsors}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Session Modal */}
      {useModals && visibleModal?.type === MODAL_TYPES.SESSION && (
        <SessionModal
          visibleSpeakerFields={visibleSpeakerFields}
          sessionData={getSessionDataForModal(
            visibleModal.id,
            scheduleData?.data?.sessionsById || {},
            scheduleData?.data?.speakersById || {}
          )}
          visibleSessionFields={visibleSessionFields}
          visibleSessionFieldsById={visibleSessionFieldsById}
          onClose={() => handleCloseModal(MODAL_TYPES.SESSION, visibleModal.id)}
          onBack={() => handleCloseModal(MODAL_TYPES.SESSION, visibleModal.id)}
          onSpeakerClick={handleSpeakerClick}
          onSubsessionClick={handleSubsessionClick}
        />
      )}

      {/* Subsession Modal (SB-8856) */}
      {useModals && visibleModal?.type === MODAL_TYPES.SUBSESSION && (
        <SubsessionModal
          visibleSpeakerFields={visibleSpeakerFields}
          subsessionData={getSubsessionDataForModal(
            visibleModal.id,
            scheduleData?.data?.sessionsById || {},
            scheduleData?.data?.speakersById || {}
          )}
          visibleSessionFields={visibleSessionFields}
          visibleSessionFieldsById={visibleSessionFieldsById}
          onClose={() => handleCloseModal(MODAL_TYPES.SUBSESSION, visibleModal.id)}
          onBack={() => handleCloseModal(MODAL_TYPES.SUBSESSION, visibleModal.id)}
          onSpeakerClick={handleSpeakerClick}
        />
      )}

      {/* Speaker Modal */}
      {useModals && visibleModal?.type === MODAL_TYPES.SPEAKER && (
        <SpeakerModal
          speakerData={getSpeakerDataForModal(
            visibleModal.id,
            scheduleData?.data?.speakersById || {},
            scheduleData?.data?.sessionsById || {}
          )}
          visibleSpeakerFields={visibleSpeakerFields}
          visibleSpeakerFieldsById={visibleSpeakerFieldsById}
          onClose={() => handleCloseModal(MODAL_TYPES.SPEAKER, visibleModal.id)}
          onBack={() => handleCloseModal(MODAL_TYPES.SPEAKER, visibleModal.id)}
          onSessionClick={handleOpenSession}
        />
      )}

      {/* Footer */}
      <Footer hidden={Boolean(scheduleData?.hideFooter)} />
    </div>
  );
};

export default ScheduleWidget;
